import { useState } from "react";
import { presalesApi } from "../../api/presalesApi";

const STAGES = [
  { key: "new",         label: "New" },
  { key: "qualified",   label: "Qualified" },
  { key: "demoFn",      label: "Demo FN" },
  { key: "demoTech",    label: "Demo Tech" },
  { key: "negotiation", label: "Negotiation" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, "0")} ${d.toLocaleString("en", { month: "short" })}`;
}

export default function BasicTab({ lead, mutate, showToast }) {
  const [pendingStage, setPendingStage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notesDraft, setNotesDraft] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savingBasic, setSavingBasic] = useState(false);
  const [form, setForm] = useState(null);

  const isClosed = lead.stage === "won" || lead.stage === "lost";

  function startEdit() {
    setForm({
      contact_name: lead.contact_name || "",
      designation: lead.designation || "",
      company_name: lead.company_name || "",
      company_type: lead.company_type || "",
      location: lead.location || "",
      email: lead.email || "",
      phone: lead.phone || "",
      source: lead.source || "",
      next_action: lead.next_action || "",
      next_action_date: lead.next_action_date ? String(lead.next_action_date).slice(0, 10) : "",
      chasing_since: lead.chasing_since ? String(lead.chasing_since).slice(0, 10) : "",
    });
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setForm(null);
  }

  async function saveBasic() {
    if (!form?.contact_name?.trim() || !form?.company_name?.trim()) {
      showToast?.("Contact name and company name are required", "error");
      return;
    }
    setSavingBasic(true);
    try {
      await presalesApi.updateLead(lead.id, {
        contact_name: form.contact_name,
        designation: form.designation,
        company_name: form.company_name,
        company_type: form.company_type,
        location: form.location,
        email: form.email,
        phone: form.phone,
        source: form.source,
        next_action: form.next_action,
        next_action_date: form.next_action_date || null,
        chasing_since: form.chasing_since || null,
      });
      setEditing(false);
      setForm(null);
      await mutate();
      showToast?.("Lead details updated", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to update lead details", "error");
    } finally {
      setSavingBasic(false);
    }
  }

  async function saveStage() {
    if (!pendingStage) return;
    setSaving(true);
    try {
      await presalesApi.setStage(lead.id, pendingStage);
      setPendingStage(null);
      await mutate();
      showToast?.("Stage updated", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to update stage", "error");
    } finally {
      setSaving(false);
    }
  }

  async function saveNotes() {
    setSavingNotes(true);
    try {
      await presalesApi.updateLead(lead.id, { notes: notesDraft });
      setNotesDraft(null);
      await mutate();
      showToast?.("Notes saved", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to save notes", "error");
    } finally {
      setSavingNotes(false);
    }
  }

  const notesValue = notesDraft !== null ? notesDraft : (lead.notes || "");

  return (
    <div>
      {/* Contact grid */}
      <div className="ps-so-section">
        <div className="ps-so-lbl">
          <span>Contact</span>
          {!editing ? (
            <button onClick={startEdit}>Edit</button>
          ) : (
            <span style={{ display: "inline-flex", gap: 8 }}>
              <button onClick={cancelEdit}>Cancel</button>
              <button onClick={saveBasic} disabled={savingBasic}>
                {savingBasic ? "Saving..." : "Save"}
              </button>
            </span>
          )}
        </div>
        {!editing ? (
          <div className="ps-contact">
            <div className="ps-kv">
              <span className="k">Name</span>
              <span className="v">{lead.contact_name}</span>
            </div>
            <div className="ps-kv">
              <span className="k">Designation</span>
              <span className="v">{lead.designation || "—"}</span>
            </div>
            <div className="ps-kv">
              <span className="k">Company</span>
              <span className="v">{lead.company_name}</span>
            </div>
            <div className="ps-kv">
              <span className="k">Company Type</span>
              <span className="v">{lead.company_type || "—"}</span>
            </div>
            <div className="ps-kv">
              <span className="k">Location</span>
              <span className="v">{lead.location || "—"}</span>
            </div>
            <div className="ps-kv">
              <span className="k">Email</span>
              <span className="v mono">{lead.email || "—"}</span>
            </div>
            <div className="ps-kv">
              <span className="k">Phone</span>
              <span className="v mono">{lead.phone || "—"}</span>
            </div>
            <div className="ps-kv">
              <span className="k">Source</span>
              <span className="v">{lead.source || "—"}</span>
            </div>
            <div className="ps-kv">
              <span className="k">Owner</span>
              <span className="v">{lead.owner_name || "—"}</span>
            </div>
            {lead.next_action && (
              <div className="ps-kv" style={{ gridColumn: "1 / -1" }}>
                <span className="k">Next Action</span>
                <span className="v">
                  {lead.next_action}
                  {lead.next_action_date && (
                    <span style={{ marginLeft: 8, fontFamily: "var(--ps-mono)", fontSize: 11, color: "var(--ps-ink-4)" }}>
                      · {fmtDate(lead.next_action_date)}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="ps-form-row two">
            <div className="ps-fld">
              <label>Name *</label>
              <input type="text" value={form.contact_name} onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Designation</label>
              <input type="text" value={form.designation} onChange={(e) => setForm((p) => ({ ...p, designation: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Company *</label>
              <input type="text" value={form.company_name} onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Company Type</label>
              <input type="text" value={form.company_type} onChange={(e) => setForm((p) => ({ ...p, company_type: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Location</label>
              <input type="text" value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Email</label>
              <input type="text" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Phone</label>
              <input type="text" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Source</label>
              <input type="text" value={form.source} onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Next Action</label>
              <input type="text" value={form.next_action} onChange={(e) => setForm((p) => ({ ...p, next_action: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Next Action Date</label>
              <input type="date" value={form.next_action_date} onChange={(e) => setForm((p) => ({ ...p, next_action_date: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Chasing Since</label>
              <input type="date" value={form.chasing_since} onChange={(e) => setForm((p) => ({ ...p, chasing_since: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Owner</label>
              <input type="text" value={lead.owner_name || ""} disabled />
            </div>
          </div>
        )}
      </div>

      {/* Stage stepper */}
      {!isClosed && (
        <div className="ps-so-section">
          <div className="ps-so-lbl">Stage</div>
          <div className="ps-stepper">
            {STAGES.map((s) => {
              const isCurrent = lead.stage === s.key;
              const isChosen  = pendingStage === s.key;
              const cls = isChosen ? "pending" : isCurrent ? "on" : "";
              return (
                <button
                  key={s.key}
                  className={`ps-step ${cls}`}
                  data-stage={s.key}
                  onClick={() => {
                    if (s.key === lead.stage) { setPendingStage(null); return; }
                    setPendingStage(isChosen ? null : s.key);
                  }}
                >
                  <span className="sw" />
                  {s.label}
                </button>
              );
            })}
          </div>
          <div className={`ps-stage-save ${pendingStage ? "on" : ""}`}>
            <span className="msg">
              Move to <strong>{STAGES.find(s => s.key === pendingStage)?.label}</strong>?
            </span>
            <div className="acts">
              <button onClick={() => setPendingStage(null)}>Cancel</button>
              <button className="save" disabled={saving} onClick={saveStage}>
                {saving ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="ps-so-section">
        <div className="ps-so-lbl">Notes</div>
        <textarea
          className="ps-notes-area"
          value={notesValue}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Add notes…"
        />
        {notesDraft !== null && (
          <div className="ps-notes-save-bar">
            <button onClick={() => setNotesDraft(null)}>Cancel</button>
            <button className="primary" disabled={savingNotes} onClick={saveNotes}>
              {savingNotes ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
